import * as React from "react";

import { AnalyticsConfiguration } from "../../analytics";

import {
  DataSourceAnalyticsConfigurationProvider,
  DataSourceAnalyticsConfigurationProviderProps,
  useDataSourceAnalyticsConfiguration,
} from "../data-source-analytics-configuration-provider";

interface AnalyticsConfigurationConsumerInterface {
  isConfigurationLoaded: boolean;
  analyticsConfiguration?: AnalyticsConfiguration;
}

const defaultValue = {} as unknown as AnalyticsConfigurationConsumerInterface;

const AnalyticsConfigurationContext = React.createContext(defaultValue);

const AnalyticsConfiguration_ConvenienceLayerProvider: React.FunctionComponent =
  (props) => {
    const { data, status } = useDataSourceAnalyticsConfiguration();

    const isConfigurationLoaded = React.useMemo(() => {
      return status === "success" || status === "error";
    }, [status]);

    const value: AnalyticsConfigurationConsumerInterface = React.useMemo(
      () => ({
        isConfigurationLoaded,
        analyticsConfiguration: data,
      }),
      [isConfigurationLoaded, data]
    );
    return (
      <AnalyticsConfigurationContext.Provider value={value}>
        {props.children || []}
      </AnalyticsConfigurationContext.Provider>
    );
  };

interface AnalyticsConfigurationProviderProps
  extends DataSourceAnalyticsConfigurationProviderProps {}

export const AnalyticsConfigurationProvider: React.FunctionComponent<
  AnalyticsConfigurationProviderProps
> = (props) => {
  return (
    <DataSourceAnalyticsConfigurationProvider
      useInitialState={props.useInitialState}
    >
      <AnalyticsConfiguration_ConvenienceLayerProvider>
        {props.children || []}
      </AnalyticsConfiguration_ConvenienceLayerProvider>
    </DataSourceAnalyticsConfigurationProvider>
  );
};

export const useAnalyticsConfiguration = () => {
  const value = React.useContext(AnalyticsConfigurationContext);

  if (value === defaultValue) {
    throw new Error(
      "useAnalyticsConfiguration must be used within a AnalyticsConfigurationProvider"
    );
  }

  return value;
};
